import { Button, Toggle } from '@/shared';
import { HousesErrorBoundary, HouseTable } from '../components';
import { useState } from 'react';
import { ManagerDetailsMenu } from '../components/manager';
import { AssignHouseModal, DeleteManagerModal } from '../components/modals';
import { useNavigate, useParams } from '@tanstack/react-router';
import {
  useAssignHouseMutation,
  useDeleteManager,
  useManagerDetailQuery,
} from '../hooks';
import { useUpdateManagerPermissions } from '../hooks/useManagerPermissions';
import { formattedDate } from '@/shared/utils/formatDate';
import type { ManagerPermissions } from '../api/managerApi';

// 1️⃣ Define the Permission type
type Permission =
  | 'manageResidents'
  | 'managePayments'
  | 'viewReports'
  | 'postAnnouncements'
  | 'approveApplications';

// 2️⃣ Define structure for permission items
interface PermissionItem {
  key: Permission;
  label: string;
  defaultValue: boolean;
  handler: (value: boolean) => void;
}

const ManagerDetailsPageContent = () => {
  const [showMenu, setShowMenu] = useState<boolean>(false);
  const [assignNewHouseModalShow, setAssignNewHouseModalShow] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);

  const { managerId } = useParams({ from: '/houses/managers/$managerId' });
  const [houseAssignmentErrors, setHouseAssignmentErrors] = useState<string[]>(
    [],
  );
  const [deleteManagerErrors, setDeleteManagerErrors] = useState<string[]>([]);
  const { data: manager, isLoading, error } = useManagerDetailQuery(managerId);
  const assignMutation = useAssignHouseMutation();
  const deleteManagerMutation = useDeleteManager();
  const navigate = useNavigate();

  // Mutation for updating permissions
  const updatePermissionsMutation = useUpdateManagerPermissions();

  // Handler for permission toggle with API call
  const handleToggle = (key: keyof ManagerPermissions) => async (value: boolean) => {
    if (!manager?.permissions) return;

    // Optimistic update - update UI immediately via API call
    const payload = { permissions: { [key]: value } };

    updatePermissionsMutation.mutate({
      managerId,
      payload,
    });
  };

  if (isLoading) return <p>Loading manager details...</p>;
  if (error) return <p>Error loading manager details</p>;
  if (!manager) return <p>No manager found</p>;

  //  Get permissions from manager data (comes from API)
  const permissions = manager.permissions || {
    manageResidents: true,
    managePayments: true,
    viewReports: true,
    postAnnouncements: true,
    approveApplications: false,
  };

  // 3️⃣ Define permission config (label + handler)
  const permissionList: PermissionItem[] = [
    {
      key: 'manageResidents',
      label: 'Can manage residents',
      defaultValue: permissions.manageResidents,
      handler: handleToggle('manageResidents'),
    },
    {
      key: 'managePayments',
      label: 'Can manage payments',
      defaultValue: permissions.managePayments,
      handler: handleToggle('managePayments'),
    },
    {
      key: 'viewReports',
      label: 'Can view reports',
      defaultValue: permissions.viewReports,
      handler: handleToggle('viewReports'),
    },
    {
      key: 'postAnnouncements',
      label: 'Can post house announcements',
      defaultValue: permissions.postAnnouncements,
      handler: handleToggle('postAnnouncements'),
    },
    {
      key: 'approveApplications',
      label: 'Can approve/reject applications',
      defaultValue: permissions.approveApplications,
      handler: handleToggle('approveApplications'),
    },
  ];

  const handleAssignHouses = (managerId: string, houseIds: string[]) => {
    console.log(
      `Assigning houses ${houseIds.join(', ')} to manager ${managerId}`,
    );
    // assignMutation.mutate({ managerId, houseIds: houseIds });
    assignMutation.mutate(
      { managerId, houseIds },
      {
        onSuccess: () => {
          // ✅ Close modal only when API succeeds
          setAssignNewHouseModalShow(false);
        },
        onError: (error: unknown) => {
          console.error('Error assigning houses:', error);
          const err = error as { message?: string };
          setHouseAssignmentErrors(
            err.message ? [err.message] : ['Failed to assign houses'],
          );
        },
      },
    );
  };

  const handleDeleteManager = (managerId: string) => {
    deleteManagerMutation.mutate(
      { managerId },
      {
        onSuccess: () => {
          setIsDeleteModalOpen(false);
          navigate({ to: '/houses/managers' });
        },
        onError: (error: unknown) => {
          console.error('Error deleting manager:', error);
          const err = error as { message?: string };
          setDeleteManagerErrors(
            err.message ? [err.message] : ['Failed to delete manager'],
          );
        },
      },
    );
  };

  return (
    <div className="min-h-screen bg-background w-full">
      {/* Header  */}
      <div className="w-full flex flex-col lg:flex-row items-center justify-between gap-4 mb-8 px-0 sm:px-6 lg:px-8">
        <div className="text-center lg:text-start w-full lg:w-[80%]">
          <h3 className="text-lg md:text-3xl font-bold mb-2 text-text-primary">
            Manager details
          </h3>
          <p className="text-gray-600 md:mb-8 text-base md:text-xl text-text-secondary">
            House management / Manage house managers / Manager details
          </p>
        </div>
        <Button
          variant="primary"
          size="lg"
          leftIcon={
            <svg
              width="14"
              height="14"
              viewBox="0 0 14 14"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M7 1V7M7 7V13M7 7H13M7 7L1 7"
                stroke="white"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          }
          // onClick={() => setIsModalOpen(true)}
        >
          Invite house manager
        </Button>
      </div>

      <div className="px-0 sm:px-6 lg:px-8">
        {/* Manger Information  */}
        <div className="grid grid-col-2 xl:grid-cols-3 gap-8 mb-4 md:mb-8">
          <div className="col-span-1">
            <div className="w-full md:w-[50%] xl:w-full mx-auto">
              <div className="w-full rounded-tr-3xl rounded-tl-3xl overflow-hidden mb-4 h-80">
                <img
                  src={manager.avatar || "/assets/images/manager-profile-placeholder.png"}
                  className="w-full h-full object-cover"
                  alt={manager.name || "Manager"}
                  onError={(e) => {
                    e.currentTarget.src = "/assets/images/manager-profile-placeholder.png";
                  }}
                />
              </div>
              <div className="flex justify-between mb-4">
                <div>
                  <h2 className="text-lg md:text-3xl text-text-primary font-bold mb-2">
                    {manager.name}
                  </h2>
                  <p className="text-base md:text-xl text-text-secondary mb-2">
                    Asst. Manager
                  </p>
                  <div className="flex justify-between">
                    <div
                      className={`text-base md:text-xl ${
                        manager.status === 'ACCEPTED' ||
                        manager.status === 'ACTIVE'
                          ? 'text-[#177D3D]'
                          : 'text-[#B02E0C]'
                      } mb-2 flex items-center gap-3`}
                    >
                      <span>
                        {manager.status === 'ACCEPTED' ||
                        manager.status === 'ACTIVE'
                          ? 'Active'
                          : 'Inactive'}
                      </span>
                      <svg
                        width="10"
                        height="5"
                        viewBox="0 0 10 5"
                        fill="none"
                        xmlns="http://www.w3.org/2000/svg"
                      >
                        <path d="M5 5L0 0H10L5 5Z" fill="#177D3D" />
                      </svg>
                    </div>
                  </div>
                </div>

                {/* share and menu  */}
                <div className="flex">
                  <div className="flex justify-center p-3 cursor-pointer mr-4">
                    <svg
                      width="18"
                      height="20"
                      viewBox="0 0 18 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M15 20C14.1667 20 13.4583 19.7083 12.875 19.125C12.2917 18.5417 12 17.8333 12 17C12 16.9 12.025 16.6667 12.075 16.3L5.05 12.2C4.78333 12.45 4.475 12.6458 4.125 12.7875C3.775 12.9292 3.4 13 3 13C2.16667 13 1.45833 12.7083 0.875 12.125C0.291667 11.5417 0 10.8333 0 10C0 9.16667 0.291667 8.45833 0.875 7.875C1.45833 7.29167 2.16667 7 3 7C3.4 7 3.775 7.07083 4.125 7.2125C4.475 7.35417 4.78333 7.55 5.05 7.8L12.075 3.7C12.0417 3.58333 12.0208 3.47083 12.0125 3.3625C12.0042 3.25417 12 3.13333 12 3C12 2.16667 12.2917 1.45833 12.875 0.875C13.4583 0.291667 14.1667 0 15 0C15.8333 0 16.5417 0.291667 17.125 0.875C17.7083 1.45833 18 2.16667 18 3C18 3.83333 17.7083 4.54167 17.125 5.125C16.5417 5.70833 15.8333 6 15 6C14.6 6 14.225 5.92917 13.875 5.7875C13.525 5.64583 13.2167 5.45 12.95 5.2L5.925 9.3C5.95833 9.41667 5.97917 9.52917 5.9875 9.6375C5.99583 9.74583 6 9.86667 6 10C6 10.1333 5.99583 10.2542 5.9875 10.3625C5.97917 10.4708 5.95833 10.5833 5.925 10.7L12.95 14.8C13.2167 14.55 13.525 14.3542 13.875 14.2125C14.225 14.0708 14.6 14 15 14C15.8333 14 16.5417 14.2917 17.125 14.875C17.7083 15.4583 18 16.1667 18 17C18 17.8333 17.7083 18.5417 17.125 19.125C16.5417 19.7083 15.8333 20 15 20ZM15 18C15.2833 18 15.5208 17.9042 15.7125 17.7125C15.9042 17.5208 16 17.2833 16 17C16 16.7167 15.9042 16.4792 15.7125 16.2875C15.5208 16.0958 15.2833 16 15 16C14.7167 16 14.4792 16.0958 14.2875 16.2875C14.0958 16.4792 14 16.7167 14 17C14 17.2833 14.0958 17.5208 14.2875 17.7125C14.4792 17.9042 14.7167 18 15 18ZM3 11C3.28333 11 3.52083 10.9042 3.7125 10.7125C3.90417 10.5208 4 10.2833 4 10C4 9.71667 3.90417 9.47917 3.7125 9.2875C3.52083 9.09583 3.28333 9 3 9C2.71667 9 2.47917 9.09583 2.2875 9.2875C2.09583 9.47917 2 9.71667 2 10C2 10.2833 2.09583 10.5208 2.2875 10.7125C2.47917 10.9042 2.71667 11 3 11ZM15 4C15.2833 4 15.5208 3.90417 15.7125 3.7125C15.9042 3.52083 16 3.28333 16 3C16 2.71667 15.9042 2.47917 15.7125 2.2875C15.5208 2.09583 15.2833 2 15 2C14.7167 2 14.4792 2.09583 14.2875 2.2875C14.0958 2.47917 14 2.71667 14 3C14 3.28333 14.0958 3.52083 14.2875 3.7125C14.4792 3.90417 14.7167 4 15 4Z"
                        fill="#474747"
                      />
                    </svg>
                  </div>
                  <button
                    className="flex justify-center p-3 cursor-pointer"
                    onClick={() => setShowMenu((prev) => !prev)}
                  >
                    <svg
                      width="4"
                      height="16"
                      viewBox="0 0 4 16"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M2 16C1.45 16 0.979167 15.8042 0.5875 15.4125C0.195833 15.0208 0 14.55 0 14C0 13.45 0.195833 12.9792 0.5875 12.5875C0.979167 12.1958 1.45 12 2 12C2.55 12 3.02083 12.1958 3.4125 12.5875C3.80417 12.9792 4 13.45 4 14C4 14.55 3.80417 15.0208 3.4125 15.4125C3.02083 15.8042 2.55 16 2 16ZM2 10C1.45 10 0.979167 9.80417 0.5875 9.4125C0.195833 9.02083 0 8.55 0 8C0 7.45 0.195833 6.97917 0.5875 6.5875C0.979167 6.19583 1.45 6 2 6C2.55 6 3.02083 6.19583 3.4125 6.5875C3.80417 6.97917 4 7.45 4 8C4 8.55 3.80417 9.02083 3.4125 9.4125C3.02083 9.80417 2.55 10 2 10ZM2 4C1.45 4 0.979167 3.80417 0.5875 3.4125C0.195833 3.02083 0 2.55 0 2C0 1.45 0.195833 0.979167 0.5875 0.5875C0.979167 0.195833 1.45 0 2 0C2.55 0 3.02083 0.195833 3.4125 0.5875C3.80417 0.979167 4 1.45 4 2C4 2.55 3.80417 3.02083 3.4125 3.4125C3.02083 3.80417 2.55 4 2 4Z"
                        fill="#474747"
                      />
                    </svg>
                  </button>

                  {/* menu  */}
                  {showMenu && (
                    <ManagerDetailsMenu
                      onAssignHouse={() => setAssignNewHouseModalShow(true)}
                      onDeleteManager={() => setIsDeleteModalOpen(true)}
                    />
                  )}
                </div>
              </div>

              <div className="mb-4">
                <p className="text-text-secondary">
                  {manager.lastActivity ? (
                    `Last Activity: ${manager.lastActivity.description} on ${formattedDate(
                      manager.lastActivity.createdAt,
                    )}`
                  ) : (
                    'No recent activity'
                  )}

                </p>
              </div>

              <div>
                <Button
                  className="w-full bg-white hover:!bg-[#5761EB] !border !border-[#5761EB] !text-[#5761EB] hover:!text-white text-bold !text-lg cursor-pointer"
                  onClick={() => setAssignNewHouseModalShow(true)}
                >
                  Assign New House
                </Button>
              </div>
            </div>
          </div>

          <div className="col-span-1 xl:col-span-2">
            <div className="grid grid-cols-2 gap-4 mb-4 md:mb-8">
              <div className="p-4 card">
                <p className="text-text-primary text-xl md:text-[28px] font-bold mb-2 ">
                  {manager.totalHouses}
                </p>
                <p className="text-text-secondary text-base md:text-xl">
                  Total houses managed
                </p>
              </div>
              <div className="p-4 card">
                <p className="text-text-primary text-xl md:text-[28px] font-bold mb-2 ">
                  {manager.totalResidents || 0}
                </p>
                <p className="text-text-secondary text-base md:text-xl">
                  Total residents managed
                </p>
              </div>
            </div>

            {/* contact  */}
            <div className="mb-4 md:mb-8">
              <h4 className="text-text-primary text-lg md:text-xl font-bold mb-4 ">
                Contact details
              </h4>

              <div className="py-2 flex items-center gap-3 mb-2">
                <div>
                  <img src="/assets/icons/phone.svg" alt="" />
                </div>
                <div>
                  <p className="text-text-secondary mb-1 text-sm md:text-base">Phone number</p>
                  <p className="text-text-primary text-base md:text-xl">{manager.phone ?? 'N/A'}</p>
                </div>
              </div>

              <div className="py-2 flex items-center justify-between gap-3 mb-2">
                <div className="py-2 flex items-center gap-3">
                  <div>
                    <img src="/assets/icons/mail.svg" alt="" />
                  </div>
                  <div>
                    <p className="text-text-secondary mb-1 text-sm md:text-base">Email address</p>
                    <p className="text-text-primary text-base md:text-xl">{manager.email}</p>
                  </div>
                </div>
                <div>
                  {/* Copy to clipboard */}
                  <button className="cursor-pointer group">
                    <svg
                      width="17"
                      className=""
                      height="20"
                      viewBox="0 0 17 20"
                      fill="none"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      <path
                        d="M6 16C5.45 16 4.97917 15.8042 4.5875 15.4125C4.19583 15.0208 4 14.55 4 14V2C4 1.45 4.19583 0.979167 4.5875 0.5875C4.97917 0.195833 5.45 0 6 0H15C15.55 0 16.0208 0.195833 16.4125 0.5875C16.8042 0.979167 17 1.45 17 2V14C17 14.55 16.8042 15.0208 16.4125 15.4125C16.0208 15.8042 15.55 16 15 16H6ZM6 14H15V2H6V14ZM2 20C1.45 20 0.979167 19.8042 0.5875 19.4125C0.195833 19.0208 0 18.55 0 18V4H2V18H13V20H2Z"
                        fill="#474747"
                      />
                    </svg>
                  </button>
                </div>
              </div>

              <div className="py-2 flex items-center gap-3">
                <div>
                  <img src="/assets/icons/calender.svg" alt="" />
                </div>
                <div>
                  <p className="text-text-secondary mb-1 text-sm md:text-base">Joining date</p>
                  <p className="text-text-primary text-base md:text-xl">
                    {formattedDate(manager.joinedAt)}
                  </p>
                </div>
              </div>
            </div>

            {/* Permissions  */}
            <div>
              <h4 className="text-text-primary text-lg md:text-xl font-bold mb-4 ">
                Permissions
              </h4>
              {permissionList.map((perm) => (
                <div key={perm.key} className="p-4 border-b border-gray-100">
                  <Toggle
                    label={perm.label}
                    value={perm.defaultValue}
                    onChange={perm.handler}
                  />
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {/* Assigned houses */}
      <div className="px-0 sm:px-6 lg:px-8">
        <h4 className="text-text-primary text-base md:text-xl font-bold mb-2">
          Assigned houses
        </h4>
      </div>

      <div className="overflow-x-auto min-h-[200px]">
        <HouseTable data={manager.houses} />
      </div>

      <AssignHouseModal
        isOpen={assignNewHouseModalShow}
        onClose={() => setAssignNewHouseModalShow(false)}
        onAssign={handleAssignHouses}
        manager={manager}
        errors={houseAssignmentErrors}
      />
      <DeleteManagerModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={handleDeleteManager}
        manager={manager}
        errors={deleteManagerErrors}
      />
    </div>
  );
};

export const ManagerDetailsPage = () => {
  return (
    <HousesErrorBoundary onRetry={() => window.location.reload()}>
      <ManagerDetailsPageContent />
    </HousesErrorBoundary>
  );
};
